/**
 * Recipe photos are stored on Vercel Blob at `recipes/{id}_0.jpg`, sized
 * 1200x800 for the detail hero. Nearly every other surface paints them into a
 * 40-180pt box, so the full-size file is 3-20x larger than the slot needs — it
 * costs bandwidth, it costs a Blob cache miss, and a 196 KB JPEG decodes slower
 * than a small one, which is what the eye actually notices while scrolling.
 *
 * The small surfaces therefore ask for a `_thumb` variant stored alongside the
 * original. This module owns the naming on both sides: the scripts that write
 * the variants and the components that read them.
 */

/**
 * Whether a recipe has a photo of its own. Only a remote URL does: an empty
 * field, the legacy bundled `images/…` mirror (nearly all of those files are
 * absent) and `/assets/recipes/…` paths all end up as the placeholder drawing.
 */
export function hasRealRecipeImage(uri?: string | null): boolean {
  return !!uri && /^https?:\/\//i.test(uri);
}

/** Width of the generated thumbnail, in physical pixels. */
export const THUMB_WIDTH = 540;
/** Height of the generated thumbnail, in physical pixels (keeps the 3:2 crop). */
export const THUMB_HEIGHT = 360;
/** JPEG quality for the thumbnail. */
export const THUMB_QUALITY = 80;

/**
 * 540px is not "2.5x a 180pt card" by accident. React Native Web paints into a
 * CSS-pixel box, so a variant sized to the CSS slot goes visibly soft on a 2x or
 * 3x display. The largest small surface is ~180pt, so the variant is sized for
 * that at 3x. The detail hero is deliberately NOT in this set — it keeps the
 * 1200x800 original, because it is the one place a user studies the photo.
 */

const THUMB_SUFFIX = '_thumb';

/** `.jpg`, `.jpeg`, `.png` or `.webp` at the end of a pathname. */
const EXTENSION = /\.(jpe?g|png|webp)$/i;

/**
 * True for the blob-hosted recipe photos this convention applies to. Anything
 * else — a source site's own URL, a local `/assets/` path, the placeholder — has
 * no variant and must be returned untouched.
 */
function isBlobRecipeImage(url: string): boolean {
  return url.includes('blob.vercel-storage.com/recipes/') && EXTENSION.test(url);
}

/**
 * The thumbnail URL for a recipe photo, or `null` when the URL is not one we
 * generate variants for.
 *
 * Returning `null` rather than a guessed URL matters: the caller uses it to
 * decide whether a thumbnail is even worth attempting, and a wrong guess would
 * cost a 404 round-trip on every render.
 */
export function thumbUrlFor(url: string | null | undefined): string | null {
  if (!url || !isBlobRecipeImage(url)) return null;
  if (url.includes(THUMB_SUFFIX + '.')) return url; // already a thumbnail
  return url.replace(EXTENSION, `${THUMB_SUFFIX}$&`);
}

/**
 * The storage pathname for a thumbnail, given the full-size pathname. Used by
 * the upload and backfill scripts so the write side cannot drift from the read
 * side above.
 */
export function thumbPathnameFor(pathname: string): string {
  if (pathname.includes(THUMB_SUFFIX + '.')) return pathname;
  return pathname.replace(EXTENSION, `${THUMB_SUFFIX}$&`);
}
